/**
 * Data Service
 *
 * Provides a unified interface for fetching JSON data files from local
 * public/resources directory. Used for both development and production builds.
 */

import { getModelReportFiles, getModelReportFilesForJurisdiction } from '@/config/models';

const LOCAL_BASE_PATH = '/resources';

/**
 * Build the local URL for a file path
 */
const buildFileUrl = (filePath: string): string => {
  const normalizedPath = filePath.startsWith('/')
    ? filePath.slice(1)
    : filePath;
  return `${LOCAL_BASE_PATH}/${normalizedPath}`;
};

/**
 * Fetch a JSON file from local resources
 *
 * @param filePath - Path to the file (e.g., 'results/dutch-bros/scan_bathroom_validated.json')
 * @param options - Optional fetch options
 * @returns Promise resolving to the parsed JSON data
 * @throws Error if the file cannot be fetched or parsed
 */
export const fetchJSON = async <T = any>(
  filePath: string,
  options?: RequestInit
): Promise<T> => {
  const url = buildFileUrl(filePath);

  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${filePath}: ${response.status} ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * Fetch multiple JSON files in parallel
 *
 * @param filePaths - Array of file paths to fetch
 * @param options - Optional fetch options
 * @returns Promise resolving to an array of parsed JSON data (null for failed fetches)
 */
export const fetchMultipleJSON = async <T = any>(
  filePaths: string[],
  options?: RequestInit
): Promise<(T | null)[]> => {
  const results = await Promise.allSettled(
    filePaths.map((path) => fetchJSON<T>(path, options))
  );

  return results.map((result) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      console.error(`Failed to fetch file:`, result.reason);
      return null;
    }
  });
};

/**
 * Check if a file exists (without fetching its contents)
 *
 * @param filePath - Path to the file
 * @returns Promise resolving to true if file exists, false otherwise
 */
export const checkFileExists = async (filePath: string): Promise<boolean> => {
  const url = buildFileUrl(filePath);

  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Load compliance report from single JSON file
 *
 * Loads compliance report and transforms it to match the expected ValidationReport format
 *
 * @param reportPath - Path to compliance report JSON
 * @returns Promise resolving to array with single transformed validation report
 */
export const loadComplianceReport = async <T = any>(
  reportPath: string = 'results/bne/604/240167_3BR-AU_v16_validated.json'
): Promise<T[]> => {
  try {
    const complianceReport = await fetchJSON<any>(reportPath);

    // Collect all unique dbIds from all rules for room_summary
    const allDbIds = new Set<number>();
    const wcDbId = complianceReport.wc_db_id;

    // Collect dbIds and try to identify fixture types from reasoning text and rule context
    const fixtureDbIds: Record<string, number[]> = {
      water_closet: [],
      walls: [],
      grab_bars: [],
      other_fixtures: [],
    };

    // Track which dbIds we've seen in specific contexts
    const dbIdContext: Record<number, string[]> = {};

    (complianceReport.rule_validations || []).forEach((rule: any) => {
      const reasoning = (rule.llm_reasoning || '').toLowerCase();
      const ruleName = (rule.rule_name || '').toLowerCase();

      (rule.involved_db_ids || []).forEach((dbId: number) => {
        allDbIds.add(dbId);

        if (!dbIdContext[dbId]) {
          dbIdContext[dbId] = [];
        }
        dbIdContext[dbId].push(reasoning);

        // Identify water closet
        if (dbId === wcDbId) {
          if (!fixtureDbIds.water_closet.includes(dbId)) {
            fixtureDbIds.water_closet.push(dbId);
          }
        }

        // Identify grab bars
        if (
          ruleName.includes('grab bar') ||
          reasoning.includes('grab bar') ||
          reasoning.includes('ada_grab_bar')
        ) {
          if (!fixtureDbIds.grab_bars.includes(dbId) && dbId !== wcDbId) {
            fixtureDbIds.grab_bars.push(dbId);
          }
        }

        // Identify walls
        if (
          ruleName.includes('location') ||
          ruleName.includes('clearance') ||
          reasoning.includes('side wall') ||
          reasoning.includes('rear wall') ||
          reasoning.includes('opposite wall')
        ) {
          if (
            !fixtureDbIds.walls.includes(dbId) &&
            dbId !== wcDbId &&
            !fixtureDbIds.grab_bars.includes(dbId)
          ) {
            fixtureDbIds.walls.push(dbId);
          }
        }

        // Identify other fixtures
        if (
          reasoning.includes('shower') ||
          reasoning.includes('sink') ||
          reasoning.includes('tub') ||
          reasoning.includes('shelf') ||
          reasoning.includes('door') ||
          ruleName.includes('overlap')
        ) {
          if (
            !fixtureDbIds.other_fixtures.includes(dbId) &&
            dbId !== wcDbId &&
            !fixtureDbIds.walls.includes(dbId) &&
            !fixtureDbIds.grab_bars.includes(dbId)
          ) {
            fixtureDbIds.other_fixtures.push(dbId);
          }
        }
      });
    });

    // Include all dbIds that aren't already classified as other fixtures
    const unclassifiedDbIds = Array.from(allDbIds).filter(
      (dbId) =>
        dbId !== wcDbId &&
        !fixtureDbIds.walls.includes(dbId) &&
        !fixtureDbIds.grab_bars.includes(dbId) &&
        !fixtureDbIds.other_fixtures.includes(dbId)
    );
    fixtureDbIds.other_fixtures.push(...unclassifiedDbIds);

    // Build room_summary structure for highlighting
    const roomSummary: any = {
      water_closet: wcDbId ? { dbId: wcDbId } : undefined,
      other_fixtures: fixtureDbIds.other_fixtures.map((dbId) => ({ dbId })),
    };

    // Add walls if identified
    if (fixtureDbIds.walls.length > 0) {
      roomSummary.water_closet = roomSummary.water_closet || {};
      roomSummary.water_closet.wall_distances = {};
      if (fixtureDbIds.walls.length >= 1) {
        roomSummary.water_closet.wall_distances.rear_wall = {
          dbId: fixtureDbIds.walls[0],
        };
      }
      if (fixtureDbIds.walls.length >= 2) {
        roomSummary.water_closet.wall_distances.side_wall = {
          dbId: fixtureDbIds.walls[1],
        };
      }
    }

    // Add grab bars if identified
    if (fixtureDbIds.grab_bars.length > 0) {
      roomSummary.grab_bars = {};
      fixtureDbIds.grab_bars.forEach((dbId, index) => {
        if (index === 0) {
          roomSummary.grab_bars.horizontal_side_wall = { dbId };
        } else if (index === 1) {
          roomSummary.grab_bars.vertical_side_wall = { dbId };
        } else if (index === 2) {
          roomSummary.grab_bars.rear_wall = { dbId };
        }
      });
    }

    // Transform compliance_report.json format to match expected ValidationReport format
    const transformedReport: any = {
      validation_metadata: {
        room_id: String(complianceReport.wc_db_id || 'unknown'),
        room_name:
          complianceReport.room_name ||
          `Unit ${complianceReport.wc_db_id || 'unknown'}`,
        standard: 'ADA 604',
        section: '604 - Water Closets and Toilet Compartments',
        validation_date:
          complianceReport.timestamp || new Date().toISOString(),
        jurisdiction: 'New Jersey',
      },
      room_summary: roomSummary,
      regulation_validation_results: (
        complianceReport.rule_validations || []
      ).map((rule: any) => {
        // Map status from compliance_report format to expected format
        let status = 'WARNING';
        if (rule.status === 'PASS') {
          status = 'PASS';
        } else if (rule.status === 'FAIL' || rule.status === 'FAILED') {
          status = 'FAIL';
        } else if (
          rule.status === 'NOT_TESTED' ||
          rule.status === 'INSUFFICIENT_DATA'
        ) {
          status = 'WARNING';
        }

        return {
          regulation_id: rule.rule_number || '',
          title: rule.rule_name || rule.rule_number || '',
          requirement: rule.rule_raw_text || '',
          status: status,
          measured_value: rule.measured_value,
          required_value: rule.required_value,
          unit: rule.unit,
          notes:
            rule.llm_reasoning ||
            (Array.isArray(rule.llm_notes)
              ? rule.llm_notes.join(' ')
              : rule.llm_notes) ||
            '',
          validation_type: 'measurement',
          visible: true,
          entities_used:
            rule.involved_db_ids?.map((dbId: number) => ({ dbId })) || [],
          confidence: rule.confidence,
          warning_type: rule.warning_type,
          warning_message: rule.warning_message,
        };
      }),
    };

    console.log(
      `Loaded compliance report for WC ${complianceReport.wc_db_id} with ${complianceReport.rule_validations?.length || 0} rules`
    );

    return [transformedReport] as T[];
  } catch (error) {
    console.error('Failed to load compliance report:', error);
    throw error;
  }
};

/**
 * Load validation reports using manifest pattern
 *
 * @param manifestPath - Path to manifest.json
 * @param fallbackFiles - Optional fallback file list if manifest fails
 * @returns Promise resolving to array of validation reports
 */
export const loadValidationReportsFromManifest = async <T = any>(
  manifestPath: string = 'grouped/manifest.json',
  fallbackFiles?: string[]
): Promise<T[]> => {
  try {
    const manifest = await fetchJSON<{ files: string[] }>(manifestPath);
    const files = manifest.files || [];

    if (files.length === 0 && fallbackFiles) {
      console.warn('Manifest has no files, using fallback');
      const manifestDir = manifestPath.substring(
        0,
        manifestPath.lastIndexOf('/')
      );
      const fallbackPaths = fallbackFiles.map((file) => {
        if (file.startsWith('/')) {
          return file.replace(/^\/resources\//, '').replace(/^\//, '');
        }
        return `${manifestDir}/${file}`.replace(/\/+/g, '/');
      });

      const reports = await fetchMultipleJSON<T>(fallbackPaths);
      const validReports = reports.filter((report) => report !== null) as T[];

      console.log(
        `Loaded ${validReports.length} validation reports from ${fallbackFiles.length} fallback files`
      );

      return validReports;
    }

    // Build file paths - handle both relative and absolute paths
    const filePaths = files.map((file) => {
      if (file.startsWith('/')) {
        return file.replace(/^\/resources\//, '').replace(/^\//, '');
      }
      const manifestDir = manifestPath.substring(
        0,
        manifestPath.lastIndexOf('/')
      );
      return `${manifestDir}/${file}`.replace(/\/+/g, '/');
    });

    const reports = await fetchMultipleJSON<T>(filePaths);
    const validReports = reports.filter((report) => report !== null) as T[];

    console.log(
      `Loaded ${validReports.length} validation reports from ${files.length} files`
    );

    return validReports;
  } catch (error) {
    if (fallbackFiles && fallbackFiles.length > 0) {
      console.warn('Manifest load failed, using fallback files:', error);
      const manifestDir = manifestPath.substring(
        0,
        manifestPath.lastIndexOf('/')
      );
      const fallbackPaths = fallbackFiles.map((file) => {
        if (file.startsWith('/')) {
          return file.replace(/^\/resources\//, '').replace(/^\//, '');
        }
        return `${manifestDir}/${file}`.replace(/\/+/g, '/');
      });

      const reports = await fetchMultipleJSON<T>(fallbackPaths);
      const validReports = reports.filter((report) => report !== null) as T[];

      console.log(
        `Loaded ${validReports.length} validation reports from ${fallbackFiles.length} fallback files`
      );

      return validReports;
    }

    throw error;
  }
};

/**
 * Transform a cloud scan JSON (chapters.404.rules format) into per-door
 * reports that match the existing RiskPanel/PDF report format.
 */
export const transformCloudScanToPerDoorReports = (cloudScan: any): any[] => {
  const rules = cloudScan?.chapters?.['404']?.rules;
  if (!rules) return [];

  // Collect all unique doors across all rules
  const doorMap = new Map<
    number,
    {
      db_id: number;
      revit_id: string | null;
      room_name: string | null;
      room_number: string | null;
      family_name: string | null;
      family_type: string | null;
      level: string | null;
      swing_x: number | null;
      swing_y: number | null;
      validations: any[];
    }
  >();

  // Rule name mapping for known 404 rules (covers both ICC A117.1 and FBC 2023 numbering)
  const ruleNameMap: Record<string, string> = {
    '404.2.2': 'Clear Width',
    '404.2.3': 'Maneuvering Clearances',
    '404.2.4': 'Maneuvering Clearances', // FBC 2023 numbering for maneuvering
    '404.2.4.1': 'Thresholds',
    '404.2.4.2': 'Sliding/Folding Door Maneuvering Clearances',
    '404.2.4.3': 'Doorway Obstructions',
    '404.2.5': 'Thresholds',           // FBC 2023 numbering for thresholds
    '404.2.6': 'Door and Gate Hardware',
    '404.2.7': 'Door and Gate Hardware', // FBC 2023 numbering for hardware
    '404.2.10': 'Vision Lites',
    '404.2.11': 'Vision Lites',
  };

  // Rule description mapping from ICC A117.1 reference document
  const ruleDescriptionMap: Record<string, string> = {
    '404.2.2':
      'Clear Width: Doorways shall have a clear opening width of 32 inches (815 mm) minimum. Clear opening width for swinging doors measured between the face of door and stop, with door open 90 degrees. Openings more than 24 inches (610 mm) in depth without doors shall provide 36 inches (915 mm) minimum clear width.\n\nNo projections into clear opening width lower than 34 inches (865 mm) above the floor. Projections between 34 inches (865 mm) and 80 inches (2030 mm) above floor shall not exceed 4 inches (100 mm).\n\nExceptions: (1) Door closers and stops permitted at 78" min AFF. (2) In alterations, 5/8" max projection permitted for latch side stop.',
    '404.2.3':
      'Maneuvering Clearances: Minimum maneuvering clearances at doors and gates shall comply with Section 404.2.3. Clearances shall include the full clear opening width and required latch-side or hinge-side clearance. Floor surface shall have slope \u2264 1:48.\n\nSwinging doors (Table 404.2.3.2 key values):\n\u2022 Front approach, Pull side: 60" perpendicular, 18" parallel\n\u2022 Front approach, Push side: 52" perpendicular, 0" parallel (+ 12" if closer & latch)\n\u2022 Hinge approach, Pull: 60" perpendicular, 36" parallel (or 54" \u00d7 42")\n\u2022 Latch approach, Pull: 48" perpendicular, 24" parallel (+ 6" if closer)\n\u2022 Latch approach, Push: 42" perpendicular, 24" parallel (+ 6" if closer)',
    '404.2.4':
      'Thresholds (Ref. Sections 302 & 303): If provided, thresholds at doorways shall be \u00bd inch (13 mm) maximum in height. Raised thresholds and changes in level at doorways shall comply with Sections 302 and 303.\n\nException: An existing or altered threshold shall be permitted to be \u00be inch (19 mm) maximum in height, provided the threshold has a beveled edge on each side with a maximum slope of 1:2 for the height exceeding \u00bc inch (6.4 mm).',
    '404.2.4.1':
      'Thresholds (Ref. Sections 302 & 303): If provided, thresholds at doorways shall be \u00bd inch (13 mm) maximum in height. Raised thresholds and changes in level at doorways shall comply with Sections 302 and 303.\n\nException: An existing or altered threshold shall be permitted to be \u00be inch (19 mm) maximum in height, provided the threshold has a beveled edge on each side with a maximum slope of 1:2 for the height exceeding \u00bc inch (6.4 mm).',
    '404.2.6':
      'Door and Gate Hardware: Handles, pulls, latches, locks and other operable parts shall have a shape that is easy to grasp with one hand and does not require tight grasping, pinching or twisting of the wrist.\n\nOperational force to retract latches or disengage hold-open devices:\n\u2022 Forward, pushing or pulling motion: 15 pounds (66.7 N) maximum\n\u2022 Rotational motion: 28 inch-pounds (315 N\u00b7cm) maximum\n\n404.2.6.1 Hardware Height: Operable parts shall be 34 inches (865 mm) minimum and 48 inches (1220 mm) maximum above the floor.',
    '404.2.10':
      'Vision Lites: Doors, gates and sidelites adjacent to doors or gates containing one or more glazing panels that permit viewing through the panels shall have the bottom of at least one panel on either the door, gate or an adjacent sidelite 43 inches (1090 mm) maximum above the floor.\n\nException: Vision lites with the lowest part more than 66 inches (1675 mm) above the floor shall not be required to comply with this section.',
    '404.2.11':
      'Vision Lites: Doors, gates, and side lights adjacent to doors or gates, containing one or more glazing panels that permit viewing through the panels shall have the bottom of at least one glazed panel located 43 inches (1090 mm) maximum above the finish floor.\n\nException: Vision lights with the lowest part more than 66 inches (1675 mm) from the finish floor or ground shall not be required to comply with 404.2.11.',
    // FBC 2023 rule descriptions (different numbering from ICC A117.1)
    '404.2.5':
      'Thresholds, if provided at doorways, shall be 1/2 inch (13 mm) high maximum. Raised thresholds and changes in level at doorways shall comply with 302 and 303.\n\nEXCEPTION: Existing or altered thresholds 3/4 inch (19 mm) high maximum that have a beveled edge on each side with a slope not steeper than 1:2 shall not be required to comply with 404.2.5.',
    '404.2.7':
      'Handles, pulls, latches, locks, and other operable parts on doors and gates shall comply with 309.4. Operable parts of such hardware shall be 34 inches (865 mm) minimum and 48 inches (1220 mm) maximum above the finish floor or ground. Where sliding doors are in the fully open position, operating hardware shall be exposed and usable from both sides.\n\nEXCEPTIONS:\n1.Existing locks shall be permitted in any location at existing glazed doors without stiles, existing overhead rolling doors or grilles, and similar existing doors or grilles that are designed with locks that are activated only at the top or bottom rail.\n2.Access gates in barrier walls and fences protecting pools, spas, and hot tubs shall be permitted to have operable parts of the release of latch on self-latching devices at 54 inches (1370 mm) maximum above the finish floor or ground provided the self-latching devices are not also self-locking devices and operated by means of a key, electronic opener, or integral combination lock.',
  };

  for (const [ruleNumber, ruleData] of Object.entries(rules) as [
    string,
    any,
  ][]) {
    const rows = ruleData.rows || [];

    for (const row of rows) {
      // Skip status-only rows (NOT_APPLICABLE, NOT_TESTABLE, etc.)
      if (!row.db_id) continue;

      if (!doorMap.has(row.db_id)) {
        doorMap.set(row.db_id, {
          db_id: row.db_id,
          revit_id: row.revit_id || null,
          room_name: row.room_name || null,
          room_number: row.room_number || null,
          family_name: row.family_name || null,
          family_type: row.family_type || null,
          level: row.level || null,
          swing_x: row.swing_x ?? null,
          swing_y: row.swing_y ?? null,
          validations: [],
        });
      }

      const door = doorMap.get(row.db_id)!;
      // Update door metadata if we get better data from this row
      if (row.room_name && !door.room_name) door.room_name = row.room_name;
      if (row.swing_x != null && door.swing_x == null)
        door.swing_x = row.swing_x;
      if (row.swing_y != null && door.swing_y == null)
        door.swing_y = row.swing_y;
      if (row.room_number && !door.room_number)
        door.room_number = row.room_number;
      if (row.family_name && !door.family_name)
        door.family_name = row.family_name;
      if (row.family_type && !door.family_type)
        door.family_type = row.family_type;

      // Determine pass/fail status from the _ok fields
      const okFields = Object.entries(row).filter(([k]) => k.endsWith('_ok'));
      const allOk =
        okFields.length > 0 && okFields.every(([, v]) => v === true);
      const anyFail = okFields.some(([, v]) => v === false);
      const status =
        okFields.length === 0
          ? 'NOT_TESTED'
          : anyFail
            ? 'FAIL'
            : allOk
              ? 'PASS'
              : 'WARNING';

      // Build measured value from numeric fields
      const skipKeys = new Set([
        'db_id',
        'level',
        'revit_id',
        'room_name',
        'room_number',
        'family_name',
        'family_type',
      ]);
      const measurements: string[] = [];
      for (const [k, v] of Object.entries(row)) {
        if (skipKeys.has(k) || k.endsWith('_ok') || k.startsWith('swing_'))
          continue;
        if (typeof v === 'number') {
          measurements.push(`${k.replace(/_/g, ' ')}: ${v}`);
        }
      }

      door.validations.push({
        rule_number: ruleNumber,
        rule_name: ruleNameMap[ruleNumber] || ruleNumber,
        rule_raw_text: ruleDescriptionMap[ruleNumber] || '',
        status,
        involved_db_ids: (() => {
          const ids = row.involved_db_ids || [row.db_id];
          if (row.involved_db_ids) console.log(`[DataService] ${ruleNumber} door ${row.db_id}: involved_db_ids from JSON =`, ids);
          return ids;
        })(),
        measured_value:
          measurements.length > 0 ? measurements.join(', ') : null,
        required_value: null,
        unit: 'inches',
        confidence: 'high',
        llm_reasoning: row.reason || null,
        // Per-row room data (may differ from door-level room data)
        room_name: row.room_name || null,
        room_number: row.room_number || null,
        // Pass through per-standard status text if present
        'ICC A117.1 Status': row['ICC A117.1 Status'] || null,
        'ADA 2010 Status': row['ADA 2010 Status'] || null,
        'TAS 2012 Status': row['TAS 2012 Status'] || null,
        'FBC 2023 Status': row['FBC 2023 Status'] || null,
      });
    }
  }

  // Convert door map to per-door reports
  return Array.from(doorMap.values()).map((door) => ({
    door_db_id: door.db_id,
    revit_id: door.revit_id,
    door_type: 'swinging',
    door_type_name:
      door.family_type || door.family_name || `Door ${door.db_id}`,
    room_name: door.room_name,
    room_number: door.room_number,
    swing_x: door.swing_x,
    swing_y: door.swing_y,
    timestamp: cloudScan.scanned_at || new Date().toISOString(),
    overall_status: door.validations.some((v: any) => v.status === 'FAIL')
      ? 'FAIL'
      : 'PASS',
    rule_validations: door.validations,
    total_rules: door.validations.length,
    pass_count: door.validations.filter((v: any) => v.status === 'PASS')
      .length,
    fail_count: door.validations.filter((v: any) => v.status === 'FAIL')
      .length,
  }));
};

/**
 * Transform Skylight chapters/subchapters/rules format into a single ValidationReport
 * with a flat regulations array for display in the Compliance Hub.
 */
const expandSkylightReportToRegulations = (data: any): any => {
  const regulations: any[] = [];
  const chapterTitles: Record<string, string> = {};

  for (const chapter of (data.chapters || [])) {
    if (chapter.id && chapter.title) {
      chapterTitles[chapter.id] = chapter.title;
    }
    for (const subchapter of (chapter.subchapters || [])) {
      for (const rule of (subchapter.rules || [])) {
        // Skip empty chapter-header rules (e.g., ruleNumber "100" with empty title and "Chapter 100" text)
        const title = (rule.title || '').trim();
        if (!title) continue;

        regulations.push({
          regulation_id: rule.ruleNumber || '',
          title,
          requirement: rule.ruleText || '',
          status: 'INFO',
          visible: true,
          notes: rule.blockers || '',
          page_number: rule.pageNumber,
          references: rule.references || [],
          chapter_id: chapter.id,
          chapter_title: chapter.title,
          subchapter_id: subchapter.id,
        });
      }
    }
  }

  return {
    report_type: 'skylight',
    timestamp: data.metadata?.exportedAt || new Date().toISOString(),
    overall_status: 'INFO',
    regulations,
    chapter_titles: chapterTitles,
  };
};

/**
 * Load compliance reports for a specific model from the config.
 * When jurisdiction is provided, uses jurisdiction-specific report sets if available.
 */
export const loadComplianceReportsForModel = async <T = any>(
  modelId: string,
  jurisdiction?: string | null
): Promise<T[]> => {
  try {
    const { getModelConfigForJurisdiction } = await import('@/config/models');
    const model = getModelConfigForJurisdiction(modelId, jurisdiction);
    if (!model || model.reportSets.length === 0) return [];

    const allFiles = getModelReportFilesForJurisdiction(modelId, jurisdiction);
    if (allFiles.length === 0) return [];

    // Load all report files and tag each with its report type
    const reportsBySet = await Promise.all(
      model.reportSets.map(async (set) => {
        if (set.files.length === 0) return [];
        const reports = await fetchMultipleJSON<any>(set.files);

        // Cloud scan format (chapters.404.rules) needs to be expanded into per-door reports
        if (set.type === 'cloud_scan') {
          const expanded = reports
            .filter(Boolean)
            .flatMap((report) => transformCloudScanToPerDoorReports(report))
            .map((report) => ({ ...report, report_type: 'door' }));
          return expanded;
        }

        // Skylight chapters format → flat regulations list
        if (set.type === 'skylight_chapters') {
          return reports
            .filter(Boolean)
            .map((report) => expandSkylightReportToRegulations(report));
        }

        // Skylight SOT → one virtual "element" report per rule validation, so the
        // Risk Panel renders each corridor occurrence as its own row
        if (set.type === 'skylight_sot') {
          // Flatten in case a file contains an array of units (all-units JSON)
          return reports.filter(Boolean).flat().flatMap((report: any, reportIdx: number) => {
            const validations = report.rule_validations || [];
            return validations.map((v: any, idx: number) => ({
              report_type: 'skylight_sot',
              wc_db_id: undefined,
              door_db_id: `${reportIdx}_${idx}`, // synthetic id so Risk Panel treats it as an element row
              unit_name: report.unit_name || report.room_name || '',
              room_name: v.room_name || report.room_name || '',
              room_number: '',
              level: v.level || report.level || '',
              element_type: 'Corridor',
              overall_status: v.status || 'WARNING',
              rule_validations: [{
                ...v,
                involved_db_ids: Array.isArray(v.involved_db_ids) ? v.involved_db_ids : [],
                involved_revit_ids: v.involved_revit_ids || [],
              }],
            }));
          });
        }

        return reports
          .filter(Boolean)
          .map((report) => ({ ...report, report_type: set.type }));
      })
    );

    const allReports = reportsBySet.flat() as T[];
    console.log(
      `Loaded ${allReports.length} compliance reports for model '${modelId}'`
    );
    return allReports;
  } catch (error) {
    console.error(
      `Error loading compliance reports for model '${modelId}':`,
      error
    );
    return [];
  }
};

// Re-export with old names for backwards compatibility during migration
export {
  fetchJSON as fetchJSONFromGCS,
  fetchMultipleJSON as fetchMultipleJSONFromGCS,
};
